/* eslint-disable camelcase */
import { call, takeLatest, put, select } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import appFirebase from 'firebase/app';
import { showErrorMessage, showInfoMessage, showSuccessMessage } from '../../helpers/logger';
import { appActions, appTypes } from '../ducks/app';
import { userService, profileService, prescriptionService, configurationService, paymentsService, locationService } from '../../api';
import { RootState } from '../ducks';
import { blackListedCountries, firebaseAppId, firebaseMeasurementId } from '../../config';
import { checkError, getMessage, PagePath, redirectToPayment, redirectWithQueryParams } from '../../helpers/util';
import { modalActions } from '../ducks/modal';
import { Currency, getCurrencyByCountry, getProductId, GroupTypesB2C, Options, parseGroupType, parsePeriod, Period, Status } from '../../helpers/product';
import 'firebase/analytics';

appFirebase.initializeApp({
  apiKey: 'AIzaSyB_ei-R-wLeu1n4V41UmYRdn6xZvcKPE-U',
  authDomain: 'fibricheck.firebaseapp.com',
  databaseURL: 'https://fibricheck.firebaseio.com',
  projectId: 'fibricheck',
  storageBucket: 'fibricheck.appspot.com',
  messagingSenderId: '324514727464',
  appId: firebaseAppId,
  measurementId: firebaseMeasurementId,
});
const analytics = appFirebase.analytics();

const getPath = (state: RootState) => state.router.location.pathname;
const getQuery = (state: RootState) => state.router.location.query;
const getUser = (state: RootState) => state.appData.user;
const getLanguage = (state: RootState) => state.appData.language;
const getGeneral = (state: RootState) => state.appData.general;
const getSubscription = (state: RootState) => state.appData.subscription;
const getPrescription = (state: RootState) => state.appData.prescription;
const getPayment = (state: RootState) => state.appData.payment;
const getCurrency = (state: RootState) => state.appData.currency;

export function* loadUser():any {
  const general: any = {};
  const path = yield select(getPath);
  const query = yield select(getQuery);
  let country: string;
  try {
    general.config = yield call(configurationService.getGeneralConfiguration, query.origin);
    const user = yield call(userService.getMe);

    if (user) {
      Sentry.setUser({ id: user.id });
      analytics.setUserId(user.id);
      const activePeriods = yield call(prescriptionService.getActivePeriods, user.id);
      const userConfiguration = yield call(configurationService.getUserConfiguration, user.id);
      const activeGroupIds = activePeriods.map((activePeriod: { group_id: any; }) => activePeriod.group_id);

      firebaseLogUserProperties(user, userConfiguration.patientConfigurations);

      if (!userConfiguration.patientConfigurations ||
        userConfiguration.patientConfigurations.find(
          (config: any) => activeGroupIds.includes(config.groupId) && [GroupTypesB2C.ESSENTIAL, GroupTypesB2C.PREMIUM].includes(config.data.groupType)
        )) {
        redirectWithQueryParams(PagePath.mySubscriptions, query);
        return;
      }

      const isIap = yield call(paymentsService.checkIfIap, user.id, userConfiguration, activeGroupIds);

      if (isIap) {
        redirectWithQueryParams(PagePath.userHasIap, query);
        return;
      }

      user.profile = yield call(profileService.getProfile, user.id);

      if (user.profile?.country) {
        country = user.profile.country;
      } else {
        country = yield call(locationService.getUserCountryByIP);
      }

      if (path === PagePath.complete && blackListedCountries && user.profile?.country) {
        if (blackListedCountries.includes(user.profile.country)) {
          redirectWithQueryParams(PagePath.noB2C, query);
          return;
        }
      }

      general.products = yield call(paymentsService.getProducts);

      if (path === PagePath.plans) {
        user.freeTrial = yield call(prescriptionService.getFreeTrialPrescription, general.config.freeTrialHash);
      } else {
        user.paymentMethod = yield call(paymentsService.getPaymentMethod, user.id);
        general.selectedGroupType = parseGroupType(String(query.package));
        general.selectedPeriod = parsePeriod(String(query.period));
      }

      yield put(appActions.loadUserSuccess(user, general, country, getCurrencyByCountry(general.config.currencies, country)));
    } else {
      country = yield call(locationService.getUserCountryByIP);
      yield put(appActions.loadUserFailure(general, country, getCurrencyByCountry(general.config.currencies, country)));
    }
  } catch (e) {
    const language = yield select(getLanguage);
    yield call(checkError, e, path, language);
    if (!country) {
      country = yield call(locationService.getUserCountryByIP);
    }
    yield put(appActions.loadUserFailure(general, country, getCurrencyByCountry(general.config.currencies, country)));
  }

  if (path === PagePath.plans && blackListedCountries?.includes(country)) {
    yield put(modalActions.toggleNoSubscriptionModal(true));
  }
}

export function* loadSubscriptions():any {
  const general: any = {};
  const path = yield select(getPath);
  const query = yield select(getQuery);
  let country: string;
  try {
    general.config = yield call(configurationService.getGeneralConfiguration, query.origin);
    const user = yield call(userService.getMe);

    if (user) {
      Sentry.setUser({ id: user.id });
      analytics.setUserId(user.id);
      const activePeriods = yield call(prescriptionService.getActivePeriods, user.id);
      const userConfiguration = yield call(configurationService.getUserConfiguration, user.id);
      const activeGroupIds = activePeriods.map((activePeriod: { group_id: any; }) => activePeriod.group_id);

      firebaseLogUserProperties(user, userConfiguration.patientConfigurations);

      if (!userConfiguration.patientConfigurations ||
        userConfiguration.patientConfigurations.filter(
          (config: any) => activeGroupIds.includes(config.groupId) && Object.values(GroupTypesB2C).includes(config.data.groupType)
        ).length === 0
      ) {
        redirectWithQueryParams(PagePath.plans, query);
        return;
      }

      const isIap = yield call(paymentsService.checkIfIap, user.id, userConfiguration, activeGroupIds);

      if (isIap) {
        redirectWithQueryParams(PagePath.userHasIap, query);
        return;
      }

      user.profile = yield call(profileService.getProfile, user.id);

      if (user.profile?.country) {
        country = user.profile.country;
      } else {
        country = yield call(locationService.getUserCountryByIP);
      }

      const currency: Currency = getCurrencyByCountry(general.config.currencies, country);

      general.products = yield call(paymentsService.getProducts);
      user.paymentMethod = yield call(paymentsService.getPaymentMethod, user.id);

      const subscriptions = yield call(prescriptionService.filterMostImportantGroup, userConfiguration, activePeriods, general.products, currency);

      general.selectedPeriod = subscriptions.subscription?.billingPeriod;
      general.selectedGroupType = subscriptions.subscription?.plan;

      if (query?.result === '1') {
        if (subscriptions.subscription?.status === Status.ACTIVE) {
          firebaseLogEventSubscription(subscriptions.subscription?.plan);
        } else {
          firebaseLogEventSubscription(subscriptions.prescription.type);
        }
      }

      yield put(appActions.loadSubscriptionsSuccess(
        user,
        general,
        subscriptions.subscription,
        subscriptions.prescription,
        country,
        currency
      ));
    } else {
      country = yield call(locationService.getUserCountryByIP);
      yield put(appActions.loadSubscriptionsFailure(country, getCurrencyByCountry(general.config.currencies, country)));
    }
  } catch (e) {
    const language = yield select(getLanguage);
    yield call(checkError, e, path, language);
    if (!country) {
      country = yield call(locationService.getUserCountryByIP);
    }
    yield put(appActions.loadSubscriptionsFailure(country, getCurrencyByCountry(general.config.currencies, country)));
  }
}

export function* startFreeTrial():any {
  const path = yield select(getPath);
  try {
    const user = yield select(getUser);

    const result = user.id ? yield call(prescriptionService.startFreeTrialPrescription, user.id, user.freeTrial) : false;

    if (result) {
      yield put(modalActions.toggleStartFreeTrialModal(true));
      yield put(appActions.startFreeTrialSuccess());
      analytics.logEvent('FreeTrial_started', { origin: 'pages_subscription' });
    } else {
      yield put(appActions.startFreeTrialFailure());
    }
  } catch (e) {
    const language = yield select(getLanguage);
    yield call(checkError, e, path, language);
    yield put(appActions.startFreeTrialFailure());
  }
}

export function* startPayment({ payload }: any):any {
  const path = yield select(getPath);
  const language = yield select(getLanguage);
  try {
    const user = yield select(getUser);
    if (!payload.stripe || !payload.elements) {
      throw new Error('Stripe has not yet loaded');
    }
    if (!user.paymentMethod && !payload.cardName) {
      yield put(appActions.startPaymentFailure('invalid_name', true));
      return;
    }

    if (!payload.agreement) {
      yield put(appActions.startPaymentFailure('', false, true));
      return;
    }

    const general = yield select(getGeneral);
    const productId = getProductId(general.products, general.selectedGroupType, general.selectedPeriod);
    const currency = yield select(getCurrency);
    const query = yield (select(getQuery));
    const stripeClientSecret = yield call(paymentsService.postPaymentIntent, user.id, productId, query, user.paymentMethod, currency);

    if (!stripeClientSecret) {
      throw new Error('payment_intent_not_yet_executed');
    }

    const cardPaymentData: any = {
      payment_method: user.paymentMethod?.stripeId || {
        card: payload.elements.getElement('cardNumber'),
        billing_details: {
          name: payload.cardName,
        },
      },
    };

    const result = yield call(payload.stripe.confirmCardPayment, stripeClientSecret, cardPaymentData);

    if (result.error) {
      yield put(appActions.startPaymentFailure(result.error.code));
      return;
    }
    // The payment has been processed!
    yield put(appActions.startPaymentSuccess());
    if (result?.paymentIntent?.status === 'succeeded') {
      showSuccessMessage(getMessage(language, 'payment_successful'));
      firebaseLogEventSubscription(general.selectedGroupType);
      yield call(paymentsService.postPaymentMethod, user.id, { stripeId: result?.paymentIntent?.payment_method || '' });
      yield call(configurationService.updateRecurringSubscription, user.id, general.selectedGroupType, general.selectedPeriod, Status.ACTIVE);
      if (path !== PagePath.mySubscriptions) {
        yield call(prescriptionService.checkPrescriptionsInterval, user.id, query);
      } else {
        const prescription = yield select(getPrescription);
        yield call(prescriptionService.checkPrescriptionsIntervalOnDate, user.id, prescription.end, query);
        yield put(modalActions.toggleInputCardDetailsModal(false));
      }
      return;
    }
    throw new Error('payment_intent_unexpected_error');
  } catch (e) {
    yield call(checkError, e, path, language);
    yield put(appActions.startPaymentFailure());
  }
}

export function* updatePaymentMethod({ payload }: any):any {
  const path = yield select(getPath);
  const language = yield select(getLanguage);
  try {
    const user = yield select(getUser);
    if (!payload.stripe || !payload.elements) {
      throw new Error('Stripe has not yet loaded');
    }
    if (!payload.cardName) {
      yield put(appActions.updatePaymentMethodFailure('invalid_name', true));
      return;
    }

    const stripeClientSecret = yield call(paymentsService.createSetupIntent);

    if (!stripeClientSecret) {
      throw new Error('setup_intent_not_yet_executed');
    }

    const cardPaymentData: any = {
      payment_method: {
        card: payload.elements.getElement('cardNumber'),
        billing_details: {
          name: payload.cardName,
        },
      },
    };

    const result = yield call(payload.stripe.confirmCardSetup, stripeClientSecret, cardPaymentData);

    if (result.error) {
      yield put(appActions.updatePaymentMethodFailure(result.error.code));
      return;
    }

    if (result?.setupIntent?.status === 'succeeded') {
      const paymentMethodResult = yield call(paymentsService.postPaymentMethod, user.id, { stripeId: result?.setupIntent?.payment_method || '' });
      if (paymentMethodResult.stripeId) {
        showInfoMessage(getMessage(language, 'card_details_updated'));
        const payment = yield select(getPayment);
        if (payment.autoRenewal) {
          yield put(appActions.enableSubscription());
        }
        yield put(appActions.updatePaymentMethodSuccess(paymentMethodResult));
        yield put(modalActions.toggleInputCardDetailsModal(false));
        return;
      }
      throw new Error('setup_intent_payment_method_unexpected_error');
    }
    throw new Error('setup_intent_unexpected_error');
  } catch (e) {
    yield call(checkError, e, path, language);
    yield put(appActions.updatePaymentMethodFailure());
  }
}

export function* startOtherPaymentMethod():any {
  const path = yield select(getPath);
  try {
    const general = yield select(getGeneral);
    const productId = getProductId(general.products, general.selectedGroupType, general.selectedPeriod, true);
    if (!productId) {
      throw new Error('no_valid_productid_provided');
    }
    const query = yield select(getQuery);
    redirectToPayment(productId, query);
    yield put(appActions.startOtherPaymentMethodSuccess());
  } catch (e) {
    const language = yield select(getLanguage);
    yield call(checkError, e, path, language);
    yield put(appActions.startOtherPaymentMethodFailure());
  }
}

export function* upgradeSubscription():any {
  const path = yield select(getPath);
  try {
    const prescription = yield select(getPrescription);
    const productId = prescription.upgradeProductId;

    if (!productId) {
      throw new Error('no_valid_productid_provided');
    }
    const general = yield select(getGeneral);
    const subscription = yield select(getSubscription);
    const user = yield select(getUser);
    if (subscription?.billingPeriod === Period.MONTHLY && general?.selectedPeriod === Period.YEARLY) {
      const updated = yield call(configurationService.updateBillingPeriod, user.id, Period.YEARLY);
      if (!updated) {
        throw new Error('UpdateBillingPeriod Error');
      } else {
        yield put(appActions.upgradeSubscriptionSuccess(Period.YEARLY));
      }
    }
    const query = yield select(getQuery);
    redirectToPayment(productId, query);
  } catch (e) {
    const language = yield select(getLanguage);
    yield call(checkError, e, path, language);
    yield put(appActions.upgradeSubscriptionFailure());
  }
}

export function* downgradeSubscription():any {
  const path = yield select(getPath);
  const language = yield select(getLanguage);
  try {
    const user = yield select(getUser);
    const general = yield select(getGeneral);
    const result = yield call(
      configurationService.updateRecurringSubscription,
      user.id,
      GroupTypesB2C.ESSENTIAL,
      general.selectedPeriod,
      Status.ACTIVE
    );

    if (result) {
      showInfoMessage(getMessage(language, 'successfully_downgraded'));
      yield put(appActions.downgradeSubscriptionSuccess(general.selectedPeriod, Status.ACTIVE, GroupTypesB2C.ESSENTIAL));
      yield put(modalActions.toggleDowngradeSubscriptionModal(false));
    } else {
      showErrorMessage(getMessage(language, 'downgrade_error'));
      yield put(appActions.downgradeSubscriptionFailure());
    }
  } catch (e) {
    yield call(checkError, e, path, language);
    yield put(appActions.downgradeSubscriptionFailure());
  }
}

export function* cancelSubscription():any {
  const path = yield select(getPath);
  const language = yield select(getLanguage);
  try {
    const user = yield select(getUser);
    const prescription = yield select(getPrescription);
    const result = yield call(
      configurationService.updateRecurringSubscription,
      user.id,
      prescription.type,
      Period.MONTHLY,
      Status.INACTIVE
    );
    if (result) {
      showInfoMessage(getMessage(language, 'recurring_cancelled'));
      yield put(appActions.cancelSubscriptionSuccess(Period.MONTHLY, Status.INACTIVE, prescription.type));
      yield put(modalActions.toggleCancelSubscriptionModal(false));
    } else {
      throw new Error(`Cancel subscription Erorr (User ${user.id})`);
    }
  } catch (e) {
    yield call(checkError, e, path, language);
    yield put(appActions.cancelSubscriptionFailure());
  }
}

export function* cancelDowngrade():any {
  const path = yield select(getPath);
  const language = yield select(getLanguage);
  try {
    const user = yield select(getUser);
    const subscription = yield select(getSubscription);
    const result = yield call(
      configurationService.updateRecurringSubscription,
      user.id,
      GroupTypesB2C.PREMIUM,
      subscription.billingPeriod,
      Status.ACTIVE
    );
    if (result) {
      showInfoMessage(getMessage(language, 'successfully_cancelled_downgrade'));
      yield put(appActions.cancelDowngradeSuccess(subscription.billingPeriod, Status.ACTIVE, GroupTypesB2C.PREMIUM));
      yield put(modalActions.toggleCancelDowngradeModal(false));
    } else {
      showErrorMessage(getMessage(language, 'cancel_downgrade_error'));
      yield put(appActions.downgradeSubscriptionFailure());
    }
  } catch (e) {
    yield call(checkError, e, path, language);
    yield put(appActions.cancelDowngradeFailure());
  }
}

export function* changePeriodSubscription():any {
  const path = yield select(getPath);
  const language = yield select(getLanguage);
  try {
    const subscription = yield select(getSubscription);
    if (!subscription.billingPeriod) {
      throw new Error('No billing Period on change');
    }
    const newPeriod: Period = subscription.billingPeriod === Period.MONTHLY ? Period.YEARLY : Period.MONTHLY;
    const user = yield select(getUser);

    if (!user.paymentMethod) {
      throw new Error('No paymentmethod on subscription');
    }

    const result = yield call(
      configurationService.updateBillingPeriod,
      user.id,
      newPeriod
    );

    if (result) {
      showInfoMessage(getMessage(language, `successfully_switched_to_${newPeriod}_billing`));
      yield put(appActions.changePeriodSubscriptionSuccess(newPeriod));
      yield put(modalActions.toggleChangePeriodModal(false));
    } else {
      throw new Error(`ChangePeriod Subscription Eror (User ${user.id})`);
    }
  } catch (e) {
    yield call(checkError, e, path, language);
    yield put(appActions.changePeriodSubscriptionFailure());
  }
}

export function* startEnableSubscription({ payload }: any):any {
  const path = yield select(getPath);
  const language = yield select(getLanguage);
  try {
    if (!payload.agreement) {
      yield put(appActions.startEnableSubscriptionFailure(true));
      return;
    }
    const user = yield select(getUser);
    yield put(appActions.startEnableSubscriptionSuccess(payload.period));
    if (user.paymentMethod) {
      yield put(modalActions.toggleConfirmAutoRenewalModal(true));
    } else {
      yield put(modalActions.toggleConfigurePaymentMethodModal(true));
    }
  } catch (e) {
    yield call(checkError, e, path, language);
    yield put(appActions.startEnableSubscriptionFailure());
  }
}

export function* enableAutoRenewal():any {
  const path = yield select(getPath);
  const language = yield select(getLanguage);
  try {
    const user = yield select(getUser);
    const prescription = yield select(getPrescription);
    const general = yield select(getGeneral);
    const result = yield call(
      configurationService.updateRecurringSubscription,
      user.id,
      prescription.type,
      general.selectedPeriod,
      Status.ACTIVE
    );
    if (result) {
      showInfoMessage(getMessage(language, 'autorenewal_successfully_enabled'));
      yield put(appActions.enableSubscriptionSuccess(general.selectedPeriod, Status.ACTIVE, prescription.type));
      yield put(modalActions.toggleConfirmAutoRenewalModal(false));
    } else {
      throw new Error(`Enable Auto Renewal subscription Erorr (User ${user.id})`);
    }
  } catch (e) {
    yield call(checkError, e, path, language);
    yield put(appActions.enableSubscriptionFailure());
  }
}

function firebaseLogEventSubscription(groupType:GroupTypesB2C) {
  analytics.logEvent('Subscription_started', { origin: 'pages_subscription' });
  if (groupType === GroupTypesB2C.PREMIUM) {
    analytics.logEvent('Premium_started', { origin: 'pages_subscription' });
  } else if (groupType === GroupTypesB2C.ESSENTIAL) {
    analytics.logEvent('Essential_started', { origin: 'pages_subscription' });
  }
}

function firebaseLogUserProperties(user:any, patientConfig:any) {
  let subscription: string = Options.No;
  let prescription = Options.No;
  let freeTrial = Options.No;

  if (user.patient_enlistments && user.patient_enlistments.length > 0) {
    for (const group of user.patient_enlistments) {
      const { groupType } = patientConfig.find((config:any) => group.group_id === config.groupId).data;
      switch (groupType) {
        case GroupTypesB2C.FREE_TRIAL:
          if (freeTrial !== Options.Active) {
            freeTrial = group.expired ? Options.Expired : Options.Active;
          }
          break;
        case GroupTypesB2C.ESSENTIAL:
        case GroupTypesB2C.PREMIUM:
          if (subscription !== Options.Premium) {
            subscription = group.expired ? Options.Expired : `${groupType.charAt(0).toUpperCase()}${groupType.substring(1)}`;
          }
          break;
        case 'physician':
          if (prescription !== Options.Active) {
            prescription = group.expired ? Options.Expired : Options.Active;
          }
          break;
        default:
      }
    }
  }
  analytics.setUserProperties({ subscription, prescription, freeTrial });
}

export default function* watcher() {
  yield takeLatest(appTypes.LOAD_USER.REQUEST, loadUser);
  yield takeLatest(appTypes.LOAD_SUBSCRIPTIONS.REQUEST, loadSubscriptions);
  yield takeLatest(appTypes.START_FREE_TRIAL.REQUEST, startFreeTrial);
  yield takeLatest(appTypes.START_PAYMENT.REQUEST, startPayment);
  yield takeLatest(appTypes.START_OTHER_PAYMENT_METHOD.REQUEST, startOtherPaymentMethod);
  yield takeLatest(appTypes.UPDATE_PAYMENT_METHOD.REQUEST, updatePaymentMethod);
  yield takeLatest(appTypes.UPGRADE_SUBSCRIPTION.REQUEST, upgradeSubscription);
  yield takeLatest(appTypes.DOWNGRADE_SUBSCRIPTION.REQUEST, downgradeSubscription);
  yield takeLatest(appTypes.CANCEL_SUBSCRIPTION.REQUEST, cancelSubscription);
  yield takeLatest(appTypes.CANCEL_DOWNGRADE.REQUEST, cancelDowngrade);
  yield takeLatest(appTypes.CHANGE_PERIOD_SUBSCRIPTION.REQUEST, changePeriodSubscription);
  yield takeLatest(appTypes.START_ENABLE_SUBSCRIPTION.REQUEST, startEnableSubscription);
  yield takeLatest(appTypes.ENABLE_SUBSCRIPTION.REQUEST, enableAutoRenewal);
}
